import { useCallback } from 'react'
import useDirectionalRegionNavigation from './useDirectionalRegionNavigation'
import { getVisibleFocusableElements, isKeyboardEditableElement } from './useKeyboardNavigation'

const DEFAULT_REGION_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Reusable region-based keyboard navigation for dashboard/member pages.
 *
 * Each page can declare regions using data attributes and provide an ordered list
 * to control ArrowUp / ArrowDown transitions between regions.
 *
 * @param {{
 *  isEnabled?: boolean,
 *  regionOrder?: string[],
 *  focusableSelectorsByRegion?: Record<string, string>,
 *  getRegionForElement?: (activeElement: HTMLElement) => string|null,
 *  getFocusablesForRegion?: (region: string) => HTMLElement[],
 *  handleKeyForRegion?: (context: {
 *    region: string,
 *    key: string,
 *    activeElement: HTMLElement,
 *    focusables: HTMLElement[],
 *    index: number,
 *  }) => boolean,
 *  onBoundary?: (context: {
 *    region: string,
 *    key: string,
 *    activeElement: HTMLElement,
 *    focusables: HTMLElement[],
 *    index: number,
 *  }) => boolean,
 *  shouldWrapOnBoundary?: (context: { region: string, key: string }) => boolean,
 * }} config
 */
const useDashboardRegionKeyboardNavigation = ({
  isEnabled = true,
  regionOrder = [],
  focusableSelectorsByRegion = {},
  getRegionForElement,
  getFocusablesForRegion,
  handleKeyForRegion,
  onBoundary,
  shouldWrapOnBoundary,
}) => {
  const resolveRegionForElement = useCallback(
    (activeElement) => {
      if (!(activeElement instanceof HTMLElement)) return null

      if (typeof getRegionForElement === 'function') {
        return getRegionForElement(activeElement)
      }

      return activeElement.closest('[data-keyboard-region]')?.getAttribute('data-keyboard-region') ?? null
    },
    [getRegionForElement]
  )

  const resolveFocusablesForRegion = useCallback(
    (region) => {
      if (typeof getFocusablesForRegion === 'function') {
        return getFocusablesForRegion(region)
      }

      if (typeof document === 'undefined') return []

      const container = document.querySelector(`[data-keyboard-region="${region}"]`)
      if (!(container instanceof HTMLElement)) return []

      const selector = focusableSelectorsByRegion[region] ?? DEFAULT_REGION_FOCUSABLE_SELECTOR
      return getVisibleFocusableElements(container, selector)
    },
    [focusableSelectorsByRegion, getFocusablesForRegion]
  )

  const routeByRegionOrder = useCallback(
    ({ region, key, activeElement, focusables, index }) => {
      if (typeof onBoundary === 'function' && onBoundary({ region, key, activeElement, focusables, index })) {
        return true
      }

      if (!regionOrder.length) return false
      if (key !== 'ArrowDown' && key !== 'ArrowUp') return false

      const regionIndex = regionOrder.indexOf(region)
      if (regionIndex < 0) return false

      const step = key === 'ArrowDown' ? 1 : -1
      let nextIndex = regionIndex + step
      let nextRegionFocusables = []

      while (nextIndex >= 0 && nextIndex < regionOrder.length) {
        nextRegionFocusables = resolveFocusablesForRegion(regionOrder[nextIndex])
        if (nextRegionFocusables.length) break
        nextIndex += step
      }

      if (!nextRegionFocusables.length) return false

      if (key === 'ArrowDown') {
        nextRegionFocusables[0]?.focus()
        return true
      }

      nextRegionFocusables[nextRegionFocusables.length - 1]?.focus()
      return true
    },
    [onBoundary, regionOrder, resolveFocusablesForRegion]
  )

  const getWrapPolicy = useCallback(
    ({ region, key }) => {
      if (typeof shouldWrapOnBoundary === 'function') {
        return shouldWrapOnBoundary({ region, key })
      }

      return key === 'ArrowLeft' || key === 'ArrowRight'
    },
    [shouldWrapOnBoundary]
  )

  useDirectionalRegionNavigation({
    isEnabled,
    getRegionForElement: resolveRegionForElement,
    getFocusablesForRegion: resolveFocusablesForRegion,
    handleKeyForRegion,
    onBoundary: routeByRegionOrder,
    shouldWrapOnBoundary: getWrapPolicy,
    isEditableElement: isKeyboardEditableElement,
  })
}

export default useDashboardRegionKeyboardNavigation
